/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * After Auth0 is ready, GET /user and keep a shared user record for the shell.
 */

import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import UserService from '../services/UserService';
import { auth0Config } from '../config/auth0Config';

/**
 * Custom hook to handle user data fetching
 * Moved from App.js to separate concerns
 */
export const useUserData = () => {
  const {
    user,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
  } = useAuth0();

  // Use ref to track if we've already fetched to prevent duplicate calls
  const hasFetchedRef = useRef(false);
  const userIdRef = useRef(null);
  const redirectingRef = useRef(false);

  useEffect(() => {
    // Only call API if user is authenticated
    if (!isAuthenticated || !user || isLoading) {
      return;
    }

    // Prevent duplicate calls for the same user
    if (hasFetchedRef.current && userIdRef.current === user.sub) {
      return;
    }

    const fetchUserData = async () => {
      try {
        // Mark as fetching to prevent duplicates
        hasFetchedRef.current = true;
        userIdRef.current = user.sub;

        const token = await getAccessTokenSilently({
          authorizationParams: {
            // Use the same audience configured in auth0Config.json
            audience: auth0Config.audience,
          },
        });
        
        await UserService.me(token, user);
        // Handle response if needed in the future
      } catch (error) {
        // Reset on error to allow retry
        hasFetchedRef.current = false;
        
        // Only redirect if user is NOT authenticated AND we haven't already initiated a redirect
        // If user is authenticated but token fetch fails, don't redirect - let AuthenticationGuard handle it
        if (!isAuthenticated && !redirectingRef.current) {
          if (error?.error === 'missing_refresh_token' || 
              error?.message?.includes('Missing Refresh Token') ||
              error?.error === 'login_required' ||
              error?.error === 'consent_required') {
            redirectingRef.current = true;            loginWithRedirect({
              appState: {
                returnTo: window.location.pathname + window.location.search,
              },
            });
            return;
          }
        }
        
        // Silently handle other auth errors (user not logged in)
        // Don't log login_required errors as they're expected for unauthenticated users
        if (error?.error && error.error !== 'login_required') {            console.error('Error loading user data:', error);
        }
      }
    };

    fetchUserData();
  }, [isLoading, isAuthenticated, user?.sub, loginWithRedirect]); // Only depend on user.sub, not the whole user object

  // Reset redirect flag when authentication state changes
  useEffect(() => {
    redirectingRef.current = false;
  }, [isAuthenticated]);

  // Reset fetch flag when user changes
  useEffect(() => {
    if (user?.sub !== userIdRef.current) {
      hasFetchedRef.current = false;
    }
  }, [user?.sub]);

  return { user, isLoading, isAuthenticated };
};
